import { t } from "@lingui/macro";
import { useResponsiveProp } from "@shopify/restyle";
import { ComponentProps, useCallback } from "react";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { Button } from "@whoosh/design-system/src/components/controls";
import { JoinWaitlistIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Text,
  VStack,
  Box,
} from "@whoosh/design-system/src/components/primitive";

export type WaitlistCtaProps = {
  clientCanFulfillRequests: boolean;
  queueSlotId: string;
  time: string;
  handleRequestedRowPress: (queueSlotId: string, time: string) => void;
};

type WaitlistDescriptionProps = {
  clientCanFulfillRequests: boolean;
  alignItems?: ComponentProps<typeof VStack>["alignItems"];
  textAlign?: ComponentProps<typeof Text>["textAlign"];
  flex?: number;
};

const WaitlistIconBadge = () => (
  <Box
    backgroundColor="surface-primary"
    width={40}
    height={40}
    borderRadius="2"
    justifyContent="center"
    alignItems="center"
  >
    <JoinWaitlistIcon height={20} width={20} />
  </Box>
);

const WaitlistDescription = ({
  clientCanFulfillRequests,
  alignItems,
  textAlign,
  flex,
}: WaitlistDescriptionProps) => (
  <VStack alignItems={alignItems} gap={1} flex={flex}>
    <Text variant="mainBaseSemiBold" textAlign={textAlign}>
      {t`Don’t see your time? Join the waitlist!`}
    </Text>
    <Text variant="mainBaseMedium" textAlign={textAlign}>
      {clientCanFulfillRequests
        ? t`If a tee time opens up, we’ll notify you immediately.`
        : t`If a tee time opens up, we’ll notify that your request was fulfilled.`}
    </Text>
  </VStack>
);

export const WaitlistCta = ({
  queueSlotId,
  time,
  clientCanFulfillRequests,
  handleRequestedRowPress,
}: WaitlistCtaProps) => {
  const trackEvent = useTrackEvent();
  const isMobile = useResponsiveProp({ base: 1, md: 0 });

  const handleJoinWaitlist = useCallback(() => {
    handleRequestedRowPress(queueSlotId, time === "00:00:00" ? "" : time);
    trackEvent("TeeSheet.WaitlistJoinButtonClicked", {
      queueSlotId,
      time,
    });
  }, [queueSlotId, time, handleRequestedRowPress, trackEvent]);

  return isMobile ? (
    <VStack
      borderWidth={2}
      borderColor="outline-main-primary"
      borderRadius="4"
      py={5}
      px={5}
      bg="surface-main-primary-subtlest"
      width="100%"
      alignItems="center"
      gap={5}
    >
      <WaitlistIconBadge />
      <WaitlistDescription
        clientCanFulfillRequests={clientCanFulfillRequests}
        alignItems="center"
        textAlign="center"
      />
      <Box width="100%">
        <Button
          variant="white"
          size="small"
          onPress={handleJoinWaitlist}
          accessibilityLabel={t`Join Waitlist button`}
        >
          {t`Join Waitlist`}
        </Button>
      </Box>
    </VStack>
  ) : (
    <HStack
      borderWidth={2}
      borderColor="outline-main-primary"
      borderRadius="4"
      py={4}
      px={5}
      bg="surface-main-primary-subtlest"
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      gap={4}
    >
      <HStack alignItems="center" gap={4} flex={1}>
        <WaitlistIconBadge />
        <WaitlistDescription
          clientCanFulfillRequests={clientCanFulfillRequests}
          flex={1}
        />
      </HStack>
      <Button
        variant="white"
        size="small"
        onPress={handleJoinWaitlist}
        accessibilityLabel={t`Join Waitlist button`}
      >
        {t`Join Waitlist`}
      </Button>
    </HStack>
  );
};
